/**
 * (B) The phone's Help app — the game's own wiki (owner, Sep 9: "lets start building a wiki/Q&A type of thing that
 * goes through how things work"). Pages are content (`content/help/*.json`); the tables in them are built here, from
 * the same constants the simulation uses, so a page can quote a rule and never drift from it.
 */
import type { HelpPage } from '../content/types';
import type { Registry } from '../content/registry';
import { KARMA } from './animals';

export interface HelpRow { what: string; amount: string; note?: string }

/** the karma ledger, both halves, read off the live numbers (docs/DESIGN_KARMA.md) */
export function karmaTable(reg: Registry, sign: '+' | '-'): HelpRow[] {
  const rows: HelpRow[] = sign === '+'
    ? [
      { what: 'Feed a stray', amount: `+${KARMA.feed}` },
      { what: 'Pet one', amount: `+${KARMA.pet}`, note: 'once every few minutes per animal' },
      { what: 'Leave a tip', amount: `up to +${KARMA.tipCap}`, note: `never less than +${KARMA.tipMin}, and worth more the bigger the tip is against everything you have — counted once per counter, so a big tip beats ten small ones` },
      { what: 'Bring a patient in', amount: `+${KARMA.ambulance}`, note: 'an ambulance shift' },
      { what: 'Put a fire out', amount: `+${KARMA.fire}` },
      { what: 'Deliver a taxi fare', amount: `+${KARMA.taxi}` },
      { what: 'Run a crew off your business', amount: `+${KARMA.extortionCleared}` },
    ]
    : [
      { what: 'Kill an animal', amount: `${KARMA.killAnimal}` },
      { what: 'Kill somebody who was no threat', amount: `${KARMA.killCivilian}`, note: 'not the police, not a gang, not a burglar in your house' },
      { what: 'Hold a place up', amount: `${KARMA.robbery}` },
      { what: 'Hurt an animal', amount: `${KARMA.hurtAnimal}` },
      { what: 'Hurt somebody who was no threat', amount: `${KARMA.hurtCivilian}`, note: 'once per person per ten seconds, however many times you hit them' },
      { what: 'Hit an animal with a car', amount: `${KARMA.hitAnimal}` },
      { what: 'Throw somebody out of their car', amount: `${KARMA.carjack}` },
    ];
  // the jobs that move it are content, so the page lists whatever the catalogue holds today
  // the jobs are content, so the page lists whatever the catalogue holds — with what the job actually asks of you, since
  // "Protection round" tells a player nothing on its own (owner, Sep 9: "be more specific in the help")
  for (const j of reg.all('job')) {
    const k = j.karmaOnDone;
    if (!k || (sign === '+' ? k < 0 : k > 0)) continue;
    const what = (j.text ?? '').replace(/\{from\}/g, 'one place').replace(/\{to\}/g, 'another').replace(/\s+/g, ' ').trim();
    rows.push({ what: `${j.name} (a job)`, amount: `${k > 0 ? '+' : ''}${k}`, note: what ? `${what.charAt(0).toLowerCase()}${what.slice(1)}` : 'on finishing the job' });
  }
  rows.sort((a, b) => Math.abs(parseFloat(b.amount.replace(/[^\d.-]/g, ''))) - Math.abs(parseFloat(a.amount.replace(/[^\d.-]/g, ''))));
  return rows;
}

/** the pages in index order */
export function helpPages(reg: Registry): HelpPage[] {
  return [...reg.all('help')].sort((a, b) => (a.order ?? 100) - (b.order ?? 100) || a.name.localeCompare(b.name));
}
